/**
 * PEMDashboardPage — landing page for Patient Experience Manager users.
 *
 * Operationalizes Carlos's 8 PEM responsibility areas from the
 * 2026-06-08 San Diego demo. See docs/roles/patient_experience_manager_2026-06-08.md.
 *
 * Surface:
 *   - 4 summary cards: follow-ups due today, SLA breaches open,
 *     follow-ups completed today, stage transitions today
 *   - Conversion KPIs (rolling 30 days): booking rate, show rate,
 *     deposit rate, treatment conversion rate
 *   - Stage Kanban: grouped columns by journey stage with patient count
 *     and SLA-breach indicator per stage
 *   - Today's follow-ups due list: every cadence touchpoint due today
 *     or earlier with one-click Complete buttons
 *
 * Talks to routes/pem.js (10 IPC channels).
 * Permission: pem:read (patient_experience role + sysadmin).
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Users, AlertCircle, CheckCircle2, TrendingUp, ArrowRight,
  Clock, Loader2, Phone, RefreshCcw, Sparkles, CalendarPlus, MapPin, X,
} from "lucide-react";

const STAGE_COLOR = {
  new_lead:              "#1f6feb",
  qualified:             "#1f6feb",
  consult_scheduled:     "#7c5210",
  consult_completed:     "#7c5210",
  deposit_paid:          "#2d6b2d",
  treatment_scheduled:   "#2d6b2d",
  treatment_in_progress: "#6b1f8b",
  treatment_completed:   "#6b1f8b",
  follow_up_24h:         "#1f6feb",
  follow_up_7d:          "#1f6feb",
  follow_up_30d:         "#1f6feb",
  follow_up_90d:         "#5a6478",
  follow_up_6mo:         "#5a6478",
  follow_up_12mo:        "#5a6478",
  wellness_continuity:   "#2d6b2d",
  lost:                  "#b00020",
  completed:             "#9aa4b2",
};

const CADENCE_LABEL = {
  "24h":  "24-Hour",
  "7d":   "7-Day",
  "30d":  "30-Day",
  "90d":  "90-Day",
  "6mo":  "6-Month",
  "12mo": "12-Month",
  custom: "Custom",
};

const OUTCOME_OPTIONS = [
  { value: "contacted_well",            label: "Contacted — patient doing well" },
  { value: "contacted_concern",         label: "Contacted — concern flagged" },
  { value: "no_answer",                 label: "No answer / left message" },
  { value: "reschedule_requested",      label: "Reschedule requested" },
  { value: "wellness_continuity_offered", label: "Wellness continuity offered" },
  { value: "review_requested",          label: "Review request sent" },
  { value: "referral_received",         label: "Referral received" },
];

export default function PEMDashboardPage({ user, onNavigate }) {
  const [reference, setReference] = useState(null);  // stage labels + groups
  const [counts, setCounts] = useState({});           // per-stage counts
  const [kpiToday, setKpiToday] = useState(null);
  const [kpi30d, setKpi30d] = useState(null);
  const [followUpsDue, setFollowUpsDue] = useState([]);
  const [mineOnly, setMineOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  // Phase 5j — My Day → Calendar: newly-closed sales ready to book.
  const [recentlyClosed, setRecentlyClosed] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [bookingFor, setBookingFor] = useState(null); // the closed-order row being booked

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = window.zignagenixAPI;
      const [ref, cnt, todayKpi, conv30, dueList, closedList, clinicList] = await Promise.all([
        api.invoke("pem:stages:reference").catch(() => null),
        api.invoke("pem:journey:counts", { mineOnly }).catch(() => ({})),
        api.invoke("pem:kpi:today").catch(() => null),
        api.invoke("pem:kpi:conversion-30d", { mineOnly }).catch(() => null),
        api.invoke("pem:follow-ups:list-due", { horizonDays: 7, mineOnly }).catch(() => []),
        api.invoke("operations-calendar:recently-closed", { days: 14, mineOnly }).catch(() => []),
        api.invoke("operations-calendar:clinics").catch(() => []),
      ]);
      setReference(ref);
      setCounts(cnt || {});
      setKpiToday(todayKpi);
      setKpi30d(conv30);
      setFollowUpsDue(Array.isArray(dueList) ? dueList : []);
      setRecentlyClosed(Array.isArray(closedList) ? closedList : []);
      setClinics(Array.isArray(clinicList) ? clinicList : []);
    } catch (e) {
      setError((e && e.message) || "Failed to load PEM dashboard");
    }
    setLoading(false);
  }, [mineOnly]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function completeFollowUp(id, outcome) {
    setBusy(id);
    try {
      const notes = window.prompt("Completion notes (optional, but recommended for the audit trail):") || "";
      const r = await window.zignagenixAPI.invoke("pem:follow-ups:complete", {
        id, outcome, notes: notes.trim(),
      });
      if (r && r.error) {
        setError(r.message || r.code);
      } else {
        await loadAll();
      }
    } catch (e) {
      setError((e && e.message) || "Failed to complete");
    }
    setBusy(null);
  }

  if (loading) return <div className="page-loading">Loading PEM Dashboard…</div>;

  return (
    <div className="page" style={{ padding: "16px 24px", maxWidth: 1500, margin: "0 auto" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, margin: 0 }}>Patient Experience Dashboard</h1>
          <p style={{ color: "#5a6478", fontSize: 13, margin: 0, marginTop: 4 }}>
            Lead nurture · Consultation coordination · Treatment coordination · Concierge service · Follow-up cadence · Wellness continuity.
            <span style={{ color: "#9aa4b2", marginLeft: 6 }}>
              "If it isn't in the CRM, it didn't happen."
            </span>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            <span>My patients only</span>
          </label>
          <button onClick={loadAll} style={btnGhost}>
            <RefreshCcw size={13} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={alertStyle("error")}><AlertCircle size={14} /> {error}</div>
      )}

      {/* ── Activity KPI cards ──────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
        <KpiCard label="Follow-ups due today" value={kpiToday ? kpiToday.follow_ups_due_today : "—"} I={Clock} color={kpiToday && kpiToday.follow_ups_due_today > 0 ? "#7c5210" : "#5a6478"} />
        <KpiCard label="SLA breaches (open)" value={kpiToday ? kpiToday.sla_breaches_open : "—"} I={AlertCircle} color={kpiToday && kpiToday.sla_breaches_open > 0 ? "#b00020" : "#2d6b2d"} />
        <KpiCard label="Follow-ups completed today" value={kpiToday ? kpiToday.follow_ups_completed_today : "—"} I={CheckCircle2} color="#2d6b2d" />
        <KpiCard label="Stage transitions today" value={kpiToday ? kpiToday.stage_transitions_today : "—"} I={ArrowRight} color="#1f6feb" />
      </div>

      {/* ── Conversion KPIs ─────────────────────────────────────── */}
      {kpi30d && (
        <div style={{
          background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 12, marginBottom: 18,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <TrendingUp size={14} color="#1f6feb" />
            <strong style={{ fontSize: 13 }}>Rolling 30-day conversion</strong>
            <span style={{ fontSize: 11, color: "#9aa4b2" }}>
              (qualified → consult booked → consult held → deposit → treatment)
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
            <ConvCard label="Qualified" value={kpi30d.qualified} sub={null} />
            <ConvCard label="Booked → Held" value={pctDisplay(kpi30d.show_rate)} sub={`${kpi30d.consults_held} / ${kpi30d.consults_booked}`} />
            <ConvCard label="Held → Deposit" value={pctDisplay(kpi30d.deposit_rate)} sub={`${kpi30d.deposits} / ${kpi30d.consults_held}`} />
            <ConvCard label="Deposit → Treatment" value={pctDisplay(kpi30d.treatment_conversion_rate)} sub={`${kpi30d.treatments} / ${kpi30d.deposits}`} />
            <ConvCard label="Lost (30d)" value={kpi30d.lost} sub={kpi30d.lost > 0 ? "review root causes" : "none"} />
          </div>
        </div>
      )}

      {/* ── Newly closed sales — ready to book (Phase 5j) ────────── */}
      {recentlyClosed.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 12, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CalendarPlus size={14} color="#2d6b2d" />
            <strong style={{ fontSize: 13 }}>Newly closed sales — ready to book</strong>
            <span style={{ fontSize: 11, color: "#9aa4b2" }}>
              last 14 days · {recentlyClosed.filter((r) => !r.booked_appointment_id).length} unbooked
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recentlyClosed.map((r) => (
              <div key={r.opportunity_id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                border: "1px solid #f0f3f7", borderRadius: 4,
                background: r.booked_appointment_id ? "#f7fbf7" : "#fffdf5",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.contact_name || r.title || `Deal #${r.opportunity_id}`}
                    {r.value != null && (
                      <span style={{ color: "#2d6b2d", marginLeft: 8, fontWeight: 700 }}>
                        ${Number(r.value).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#5a6478" }}>
                    Closed {new Date(r.won_at).toLocaleDateString()}
                    {r.contact_phone ? ` · ${r.contact_phone}` : ""}
                    {r.title && r.contact_name ? ` · ${r.title}` : ""}
                  </div>
                </div>
                {r.booked_appointment_id ? (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#2d6b2d", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <CheckCircle2 size={12} />
                    Booked {new Date(r.booked_start_time).toLocaleDateString()}
                    {r.booked_clinic_name ? ` · ${r.booked_clinic_name}` : ""}
                  </span>
                ) : (
                  <button onClick={() => setBookingFor(r)} style={btnPrimary}>
                    <CalendarPlus size={11} /> Schedule
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Booking modal (Phase 5j) ──────────────────────────────── */}
      {bookingFor && (
        <BookClosedOrderModal
          order={bookingFor}
          clinics={clinics}
          onClose={() => setBookingFor(null)}
          onBooked={async () => { setBookingFor(null); await loadAll(); }}
        />
      )}

      {/* ── Stage Kanban + Follow-ups due (two columns) ───────── */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18 }}>

        {/* ── Kanban (left) ──────────────────────────────────── */}
        <div>
          <h2 style={{ fontSize: 15, margin: 0, marginBottom: 8 }}>Journey Stages</h2>
          {reference && reference.groups ? (
            reference.groups.map((g) => (
              <StageGroup
                key={g.label}
                group={g}
                labels={reference.labels}
                counts={counts}
              />
            ))
          ) : (
            <div style={{ color: "#9aa4b2", padding: 14 }}>Loading stage reference…</div>
          )}
        </div>

        {/* ── Follow-ups due (right) ─────────────────────────── */}
        <div>
          <h2 style={{ fontSize: 15, margin: 0, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Phone size={13} /> Follow-ups due (next 7 days)
            <span style={{ fontSize: 11, color: "#9aa4b2" }}>{followUpsDue.length}</span>
          </h2>
          {followUpsDue.length === 0 ? (
            <div style={{ padding: 18, color: "#9aa4b2", fontStyle: "italic", textAlign: "center", border: "1px dashed #c9d6e8", borderRadius: 4 }}>
              No follow-ups due. Either you're caught up, or no patients have reached the treatment-completed milestone yet.
            </div>
          ) : (
            <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, maxHeight: 600, overflowY: "auto" }}>
              {followUpsDue.map((f) => (
                <FollowUpRow
                  key={f.id}
                  followUp={f}
                  onComplete={completeFollowUp}
                  busy={busy === f.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={complianceFooter}>
        Every transition and follow-up completion writes to phi_access_log and audit_log per HIPAA §164.312(b).
        Auto-generated 24h / 7d / 30d / 90d / 6mo / 12mo cadence per Carlos's 2026-06-08 PEM role definition.
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function KpiCard({ label, value, I, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {I && <I size={16} color={color} />}
        <div style={{ fontSize: 11, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4 }}>{label}</div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color }}>{value}</div>
    </div>
  );
}

function ConvCard({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2, color: "#1f2a3b" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#9aa4b2", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StageGroup({ group, labels, counts }) {
  const groupTotal = group.stages.reduce((sum, s) => sum + (counts[s] ? counts[s].total : 0), 0);
  const groupSlaBreaches = group.stages.reduce((sum, s) => sum + (counts[s] ? counts[s].sla_breached : 0), 0);

  return (
    <div style={{ marginBottom: 10, border: "1px solid #e1e6ed", borderRadius: 4, background: "#fff" }}>
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #f0f3f7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#3a4a5e" }}>
          {group.label}
        </strong>
        <span style={{ fontSize: 11, color: "#5a6478" }}>
          {groupTotal} total
          {groupSlaBreaches > 0 && (
            <span style={{ color: "#b00020", fontWeight: 600, marginLeft: 8 }}>
              ⚠ {groupSlaBreaches} SLA
            </span>
          )}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${group.stages.length}, 1fr)`, gap: 0 }}>
        {group.stages.map((s) => {
          const c = counts[s] || { total: 0, sla_breached: 0 };
          const color = STAGE_COLOR[s] || "#5a6478";
          return (
            <div key={s} style={{
              padding: "10px 12px", borderRight: "1px solid #f0f3f7",
              borderLeft: `3px solid ${color}`, background: c.sla_breached > 0 ? "#fff7f7" : "transparent",
            }}>
              <div style={{ fontSize: 10, color: "#5a6478" }}>{labels[s] || s}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>{c.total}</div>
              {c.sla_breached > 0 && (
                <div style={{ fontSize: 10, color: "#b00020", marginTop: 2, fontWeight: 600 }}>
                  ⚠ {c.sla_breached} breached
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FollowUpRow({ followUp, onComplete, busy }) {
  const f = followUp;
  const due = new Date(f.due_at);
  const overdue = due.getTime() < Date.now();
  const cadence = CADENCE_LABEL[f.cadence_marker] || f.cadence_marker;
  const name = `${f.first_name || ""} ${f.last_name || ""}`.trim() || `Contact ${f.contact_id}`;
  const [showOutcome, setShowOutcome] = useState(false);

  return (
    <div style={{
      padding: "10px 12px", borderBottom: "1px solid #f0f3f7",
      background: overdue ? "#fff7f7" : "transparent",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
          background: overdue ? "#b00020" : "#1f6feb", color: "#fff",
          textTransform: "uppercase", letterSpacing: 0.3,
        }}>{cadence}</span>
        {overdue && <span style={{ fontSize: 10, color: "#b00020", fontWeight: 600 }}>OVERDUE</span>}
        <strong style={{ fontSize: 13 }}>{name}</strong>
      </div>
      <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 4 }}>
        Due: {due.toLocaleString()}
        {f.phone && <span> · {f.phone}</span>}
      </div>
      {!showOutcome ? (
        <button onClick={() => setShowOutcome(true)} disabled={busy} style={btnPrimary}>
          {busy ? <><Loader2 size={11} className="spin" /> …</> : "Complete"}
        </button>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
          {OUTCOME_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => onComplete(f.id, o.value)}
              disabled={busy}
              style={btnOutcome}
              title={o.label}
            >
              {o.label}
            </button>
          ))}
          <button onClick={() => setShowOutcome(false)} style={{ ...btnGhost, fontSize: 11 }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function pctDisplay(v) {
  if (v == null) return "—";
  return `${v}%`;
}

// ─── Phase 5j: one-click booking modal ──────────────────────────────────
// Pre-populated from the closed order; clinic dropdown is REQUIRED per
// Daniel's spec — the booking lands on the global Operations Calendar.

function BookClosedOrderModal({ order, clinics, onClose, onBooked }) {
  const [clinicId, setClinicId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(60);
  const [apptType, setApptType] = useState("treatment");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    setErr("");
    if (!clinicId) { setErr("Select a clinic — every booking needs one."); return; }
    if (!date) { setErr("Pick a date."); return; }
    const start = new Date(`${date}T${time || "09:00"}:00`);
    if (Number.isNaN(start.getTime())) { setErr("Invalid date/time."); return; }
    setSaving(true);
    try {
      const r = await window.zignagenixAPI.invoke("operations-calendar:book-closed-order", {
        opportunityId: order.opportunity_id,
        clinicId: parseInt(clinicId, 10),
        startTime: start.toISOString(),
        durationMinutes: duration,
        appointmentType: apptType,
        notes: notes.trim() || null,
      });
      if (r && r.error) setErr(r.message || r.code);
      else await onBooked();
    } catch (e) {
      setErr((e && e.message) || "Booking failed");
    }
    setSaving(false);
  }

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000, background: "rgba(17,24,39,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 6, maxWidth: 440, width: "100%", padding: 18,
        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <CalendarPlus size={15} color="#2d6b2d" />
          <strong style={{ fontSize: 14 }}>Book treatment — {order.contact_name || `Deal #${order.opportunity_id}`}</strong>
          <button onClick={onClose} aria-label="Close" style={{ ...btnGhost, marginLeft: "auto", padding: "3px 7px" }}><X size={12} /></button>
        </div>
        <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 12 }}>
          Sales order #{order.opportunity_id}
          {order.value != null ? ` · $${Number(order.value).toLocaleString()}` : ""} · closed {new Date(order.won_at).toLocaleDateString()}.
          The booking lands on the Operations Calendar immediately.
        </div>

        <label style={fieldLabel}><MapPin size={11} /> Clinic (required)</label>
        <select value={clinicId} onChange={(e) => setClinicId(e.target.value)} style={fieldInput}>
          <option value="">Select clinic…</option>
          {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={fieldLabel}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={fieldInput} />
          </div>
          <div>
            <label style={fieldLabel}>Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={fieldInput} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={fieldLabel}>Duration</label>
            <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} style={fieldInput}>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={240}>4 hours</option>
              <option value={480}>Full day</option>
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Type</label>
            <select value={apptType} onChange={(e) => setApptType(e.target.value)} style={fieldInput}>
              <option value="treatment">Treatment</option>
              <option value="consultation">Consultation</option>
              <option value="follow_up">Follow-up</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <label style={fieldLabel}>Notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...fieldInput, resize: "vertical" }} />

        {err && <div style={alertStyle("error")}><AlertCircle size={13} /> {err}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }}>
            {saving ? <><Loader2 size={12} className="spin" /> Booking…</> : "Confirm booking"}
          </button>
        </div>
      </div>
    </div>
  );
}

const fieldLabel = {
  display: "flex", alignItems: "center", gap: 4,
  fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "8px 0 3px",
};
const fieldInput = {
  width: "100%", padding: "6px 8px", fontSize: 12, color: "#1f2a3b",
  border: "1px solid #c9d6e8", borderRadius: 3, background: "#fff", boxSizing: "border-box",
};

// ─── Styles ───────────────────────────────────────────────────────────
const btnGhost = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#fff", color: "#3a4a5e",
  border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const btnPrimary = {
  padding: "4px 10px", fontSize: 11, fontWeight: 600,
  background: "#1f6feb", color: "#fff",
  border: "none", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const btnOutcome = {
  padding: "4px 8px", fontSize: 10, fontWeight: 600,
  background: "#fff", color: "#1f6feb",
  border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
};
function alertStyle(kind) {
  return {
    padding: "6px 10px", borderRadius: 3, fontSize: 12, marginBottom: 12,
    display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
const complianceFooter = {
  marginTop: 18, padding: "10px 12px", fontSize: 11, color: "#5a6478",
  background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 4,
};
